import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Home,
    User as UserIcon,
    Wallet,
    Calendar,
    CreditCard,
    ClipboardCheck,
    ClipboardList,
    AlertTriangle,
    FileText,
    Receipt,
    Bell,
    Building2,
    CheckCircle,
} from "lucide-react";
import {
    getCustomerSales,
    getCustomerSalesInstallment,
} from "../../services/sales";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axiosInstance from "../../services/api/axios";
import PropertyDetailsTab from "./SoldpropertyTabs/PropertyDetailsTab";
import PaymentInstallmentsTab from "./SoldpropertyTabs/PaymentInstallmentsTab";
import AgreementsTab from "./SoldpropertyTabs/AgreementsTab";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Receipts from "./SoldpropertyTabs/Receipts";
import CancellationDetailsTab from "./SoldpropertyTabs/CancellationDetailsTab";
import { Button } from "@material-tailwind/react";
import { GradientButton, PrimaryButton } from "../../components/Buttons";
import { Edit } from "lucide-react";
import PaymentReceiptPreviewModal from "./SoldpropertyTabs/Forms/PaymentReceiptPreviewModal";
import { FormModal } from "../../components/form";
import CustomerNotificationHistory from "./SoldpropertyTabs/CustomerNotificationHistory";
import SSPPassphraseModal from "../../components/SSPPassphraseModal";
import { fetchProjects } from "../../services/projects";
import usePrivilege from "../../hooks/usePrivilege";
import useSubscriptionFeatures from "../../hooks/useSubscriptionFeatures";
import { SUB_FEATURES } from "../../constants/subscriptionFeatures";
// F16 (v2): Execution tab
import UnitExecutionDashboard from "../Projects/UnitExecutionDashboard";

// Which receiptType values open the polymorphic receipt-preview modal vs.
// the Quill editor fallback. Mirrors the BE's CustomerSalesPaymentType enum.
const PAYMENT_RECEIPT_TYPES = new Set([
    "TOKEN_PAYMENT",
    "DOWN_PAYMENT",
    "SCHEDULED_PAYMENT",
    "FULL_PAYMENT",
    "SLAB_WISE_PAYMENT",
]);

const SoldPropertyDetails = () => {
    const { hasPrivilege } = usePrivilege();
    const { hasFeature } = useSubscriptionFeatures();
    // On BuildVizer Plus the developer-side CRM document workflows are
    // disabled: agreements, receipts, customer notifications, and the
    // cancellation flow (which is also a CRM doc step) drop out of the
    // sold-property tab list. The Installments tab stays so the tenant
    // can still view the schedule generated by their original sale.
    const showCrmDocTabs = hasFeature(SUB_FEATURES.CUSTOMER_DOCUMENTS);
    const { customerSaleId } = useParams();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('projectId');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [installments, setInstallments] = useState([]);
    const [activeTab, setActiveTab] = useState("property");
    const [hasUnsavedScheduleInstallmentChanges, setHasUnsavedScheduleInstallmentChanges] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [receiptContent, setReceiptContent] = useState("");
    const [editingInstallmentId, setEditingInstallmentId] = useState(null);
    const [, setOriginalInstallmentData] = useState(null);
    const [currentInstallmentData, setCurrentInstallmentData] = useState(null);
    const [formData, setFormData] = useState({});
    const [property, setProperty] = useState(null);
    const [sale, setSale] = useState(null);
    const [customerId, setCustomerId] = useState(null); // Add this state
    const [isHandover, setIsHandover] = useState(null);
    const [isCancelled, setIsCancelled] = useState(null);
    const [receiptType, setReceiptType] = useState(null);
    const [showSSPModal, setShowSSPModal] = useState(false); // For SSP modal
    const [sspLoading, setSspLoading] = useState(false);
    const [, setPassphrase] = useState("");
    const [pendingSendContent, setPendingSendContent] = useState(null); // Store content to send after passphrase
    // Bumped after a successful send so the installments grid (which owns its
    // own paginated fetch in PaymentInstallmentsTab) refetches immediately —
    // the "Sent" state then shows without a manual refresh / tab switch.
    const [installmentsRefreshNonce, setInstallmentsRefreshNonce] = useState(0);


    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const getNotificationType = (paymentType) => {
        switch (paymentType?.type) {
            case "TOKEN_PAYMENT":
                return "TOKEN_PAYMENT";
            case "DOWN_PAYMENT":
                return "DOWN_PAYMENT";
            case "SCHEDULED_PAYMENT":
                return "SCHEDULED_PAYMENT";
            case "FULL_PAYMENT":
                return "FULL_PAYMENT";
            case "SLAB_WISE_PAYMENT":
                return "SLAB_WISE_PAYMENT";
            default:
                return "TOKEN_PAYMENT";
        }
    };

    const handleGenerateReceipt = async (params) => {
        // Directly open the preview modal, no SSP modal
        const installmentData = params.data;
        setCurrentInstallmentData(installmentData);
        setReceiptType(installmentData.paymentType?.type || "DEFAULT");
        const notificationType = getNotificationType(installmentData.paymentType);
        try {
            const response = await axiosInstance.post("/generate/receipt-preview", {
                notificationType: notificationType,
                customerSalesId: installmentData.customerSales.customerSalesId,
                customerSalesInstallmentId: installmentData.customerSalesInstallmentId,
            });
            setReceiptContent(response);
            setModalIsOpen(true);
        } catch (error) {
            toast.error(error.message || "Failed to generate receipt");
        }
    };

    // On click of Send in any receipt/letter modal, open SSP modal and store content
    const handleSendReceipt = (htmlContent) => {
        setPendingSendContent(htmlContent || receiptContent);
        setShowSSPModal(true);
    };

    // On click of Verify & Send in SSP modal, call the send API directly
    const handleSSPConfirm = async (enteredPassphrase) => {
        setSspLoading(true);
        try {
            if (!currentInstallmentData && !sale) {
                toast.error("No data available to send receipt.");
                setSspLoading(false);
                return;
            }

            let notificationType = "";
            let customerSalesId = null;
            let customerSalesInstallmentId = null;

            if (currentInstallmentData) {
                // For receipts (Token, Down, Full, Scheduled, Slab payments)
                notificationType = getNotificationType(currentInstallmentData.paymentType);
                customerSalesId = currentInstallmentData.customerSales?.customerSalesId;
                customerSalesInstallmentId = currentInstallmentData.customerSalesInstallmentId;
            } else if (sale && receiptType) {
                // For letters (Booking, Handover, etc.)
                switch (receiptType) {
                    case "BOOKING_LETTER":
                        notificationType = "BOOKING_LETTER";
                        break;
                    case "HANDOVER_LETTER":
                        notificationType = "PROPERTY_HANDOVER_LETTER";
                        break;
                    case "CANCELLATION_LETTER":
                        notificationType = "CANCELLATION_LETTER";
                        break;
                    default:
                        notificationType = receiptType;
                }
                customerSalesId = sale.customerSalesId;
                customerSalesInstallmentId = null; // Letters don't have installment ID
            }

            const body = {
                notificationType,
                customerSalesId,
                ...(customerSalesInstallmentId && { customerSalesInstallmentId }), // Only include if it exists
                content: pendingSendContent,
                passphrase: enteredPassphrase,
            };

            await axiosInstance.post("/generate/send/receipt-notification", body);
            toast.success("Sent successfully");
            setShowSSPModal(false);
            setSspLoading(false);
            setModalIsOpen(false);
            setPendingSendContent(null);
            setPassphrase("");
            // Refresh installments immediately so the row flips to "Sent"
            // without a manual refresh: refetch the parent copy AND signal the
            // tab (which owns a separate paginated fetch) to refetch too.
            if (customerSalesId) fetchInstallments(customerSalesId);
            setInstallmentsRefreshNonce((n) => n + 1);
        } catch (error) {
            // Handle specific SSL certificate error
            if (error.response?.status === 400 && error.response?.data?.detail === "SSL certificate not generated for the customer") {
                toast.error("SSL certificate not generated for the customer. Please contact support.");
            } else if (error.response?.status === 402 && error.response?.data?.detail === "Invalid passphrase for private key") {
                toast.error("Invalid passphrase for private key. Please check your SSL passphrase and try again.");
            } else {
                toast.error(error.response?.data?.message || error.message || "Failed to send");
            }
            setSspLoading(false);
        }
    };

    const handleSSPClose = () => {
        setShowSSPModal(false);
        setSspLoading(false);
        setPassphrase("");
        setPendingSendContent(null);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setReceiptContent("");
        setCurrentInstallmentData(null);

    };

    const handleQuillChange = (content) => {
        setReceiptContent(content);
    };

    const fetchInstallments = async (customerSalesId) => {
        try {
            const installmentResponse = await getCustomerSalesInstallment({ customer_sales_id: customerSalesId });
            if (installmentResponse && Array.isArray(installmentResponse)) {
                const fetchedInstallments = installmentResponse.map((item) => ({
                    ...item,
                    isRowDisabled: item.isGeneratedSent === true || sale?.propertyData?.isHandover === true || sale?.cancellationStatus === "Initiated",

                    paymentType: item.paymentType ? item.paymentType : {},
                    customerSales: {
                        customerSalesId: customerSalesId,
                    },
                }));
                setInstallments(fetchedInstallments);
            } else {
                setInstallments([]);
            }
        } catch (error) {
            console.error("Error fetching installments:", error);
            toast.error("Failed to fetch installments: " + (error.message || error));
            setInstallments([]);
        }
    };

    const handleSaveClick = async (params) => {
        const installmentData = params.data;
        const {
            customerSalesInstallmentId,
            projectedAmount,
            paymentType,
            statusType,
            customerSales,
        } = installmentData;
        let projectedDate = params.data.projectedDate;
        let receivedDate = params.data.receivedDate;

        const payload = {
            customerSalesId: customerSales?.customerSalesId,
            projectedAmount: parseFloat(projectedAmount),
            receivedAmount: parseFloat(params.data.receivedAmount),
            projectedDate: projectedDate
                ? new Date(projectedDate).toISOString().split("T")[0]
                : null,
            receivedDate: receivedDate
                ? new Date(receivedDate).toISOString().split("T")[0]
                : null,
            details: params.data.details,
            paymentTypeId: paymentType?.paymentTypeId || null,
            statusTypeId: statusType?.statusTypeId || statusType || null,
        };

        try {
            const response = await axiosInstance.put(`/customer/sales/installment`, payload, {
                params: { customer_sales_installment_id: customerSalesInstallmentId },
            });
            toast.success("Installment updated successfully");

            setInstallments(prevInstallments =>
                prevInstallments.map(installment =>
                    installment.customerSalesInstallmentId === customerSalesInstallmentId
                        ? { ...installment, isRowDisabled: response.isGeneratedSent }
                        : installment
                )
            );
            setEditingInstallmentId(null);
            setOriginalInstallmentData(null);
        } catch (error) {
            console.error("Error updating installment:", error);
            toast.error(error.message || "Failed to update installment");
        }
    };

    const handleEditClick = (params) => {
        if (editingInstallmentId === params.data.customerSalesInstallmentId) {
            handleSaveClick(params);
        } else {
            setOriginalInstallmentData(params.data);
            setEditingInstallmentId(params.data.customerSalesInstallmentId);
        }
    };

    const isRowEditing = (params) => {
        return editingInstallmentId === params.data.customerSalesInstallmentId;
    };
    const generatePaymentType = useCallback((params) => {
        if (!params.data || !params.data.paymentType) {
            return "N/A";
        }

        const paymentType = params.data.paymentType?.type;
        // v2 (F8/§D,§E): wbsStage embed replaces statusType; fall back for backward compat
        const stageEmbed = params.data.wbsStage || params.data.statusType?.statusType || params.data.statusType;
        const stageName = stageEmbed?.typeName;
        const stageTarget = params.data.floorTarget ?? params.data.statusType?.target;

        // If the type is SLAB_WISE_PAYMENT and a stage name exists
        if (paymentType === "SLAB_WISE_PAYMENT" && stageName) {
            if (stageName === "SLAB") {
                // If stageName is SLAB, return "SLAB - floorTarget"
                return `${stageName} - ${stageTarget ?? ""}`;
            } else if (stageName === "CUSTOM" && (params.data.wbsStage?.particulars || params.data.statusType?.particulars)) {
                return params.data.wbsStage?.particulars || params.data.statusType?.particulars;
            }
            else{
                return stageEmbed?.label || stageName;
            }
        } 

        // Special logic for FULL_PAYMENT
        if (paymentType === "FULL_PAYMENT") {
            const fullPaymentInstallments = installments.filter(
                (row) => row.paymentType?.type === "FULL_PAYMENT"
            );

            const index = fullPaymentInstallments.findIndex(
                (installment) =>
                    installment.customerSalesInstallmentId ===
                    params.data.customerSalesInstallmentId
            );

            return index !== -1 ? `Installment ${index + 1}` : "N/A";
        }

        // Fallback for all other types
        return paymentType || "N/A";
    }, [installments]);
    const gridColumns = useMemo(
        () => [
            {
                headerName: "Payment Type",
                field: "paymentType.type",
                minWidth: 200,
                flex: 1,
                resizable: true,
                sortable: true,
            },
            {
                headerName: "Amount",
                field: "amountSplit",
                minWidth: 100,
                flex: 1,
                resizable: true,
                sortable: true,
                cellRenderer: (params) => {
                    const bankAmount = params.data.installmentBankAmount || 0;
                    const userAmount = params.data.installmentUserAmount || 0;
                    const projectedAmount = params.data.projectedAmount || 0;

                    if (bankAmount > 0 && userAmount > 0) {
                        return (
                            <div className="flex items-center gap-2 py-2">
                                <span className="text-gray-700">₹{projectedAmount.toLocaleString('en-IN')}</span>
                                <span className="text-xs text-gray-500 whitespace-nowrap">(Bank: ₹{bankAmount.toLocaleString('en-IN')} + User: ₹{userAmount.toLocaleString('en-IN')})</span>
                            </div>
                        );
                    }

                    return (
                        <div className="flex items-center py-2">
                            <span className="text-gray-700 font-medium">₹{projectedAmount.toLocaleString('en-IN')}</span>
                        </div>
                    );
                }
            },
            {
                headerName: "Payment Source",
                field: "paymentSource",
                minWidth: 150,
                flex: 1,
                resizable: true,
                sortable: true,
                cellRenderer: (params) => {
                    const paymentType = params.data.paymentType?.type;
                    const bankAmount = params.data.installmentBankAmount || 0;
                    const userAmount = params.data.installmentUserAmount || 0;

                    // Token and Down Payment are always user payments
                    if (paymentType === "TOKEN_PAYMENT" || paymentType === "DOWN_PAYMENT") {
                        return (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">User</span>
                        );
                    }

                    // For other payment types, show based on the split
                    if (bankAmount > 0 && userAmount === 0) {
                        return (
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Bank</span>
                        );
                    } else if (bankAmount === 0 && userAmount > 0) {
                        return (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">User</span>
                        );
                    } else if (bankAmount > 0 && userAmount > 0) {
                        return (
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Split</span>
                        );
                    }

                    return (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Not Set</span>
                    );
                }
            },
            {
                headerName: "Projected Date",
                field: "projectedDate",
                minWidth: 150,
                flex: 1,
                resizable: true,
                sortable: true,
                valueFormatter: (params) =>
                    params.value ? new Date(params.value).toLocaleDateString('en-GB') : null,
            },
            {
                headerName: "Details",
                field: "details",
                flex: 2,
                minWidth: 100,
                resizable: true,
                sortable: true,
                editable: (params) =>
                    isRowEditing(params) && !params.data.isRowDisabled,
                cellEditor: "agTextCellEditor",
            },
            {
                headerName: "Received Amount",
                field: "receivedAmount",
                minWidth: 150,
                flex: 1,
                resizable: true,
                sortable: true,
                editable: (params) =>
                    isRowEditing(params) && !params.data.isRowDisabled,
                cellEditor: "agTextCellEditor",
            },
            {
                headerName: "Received Date",
                field: "receivedDate",
                minWidth: 150,
                flex: 1,
                resizable: true,
                sortable: true,
                editable: (params) =>
                    isRowEditing(params) && !params.data.isRowDisabled,
                cellEditor: "agDateStringCellEditor",
                valueFormatter: (params) =>
                    params.value ? new Date(params.value).toLocaleDateString('en-GB') : null,
            },
            {
                headerName: "Receipt",
                cellRenderer: (params) => {
                    if (!params?.data?.receivedDate) return null;

                    // Already generated & sent — show a clear "Sent" badge
                    // instead of a (greyed) Generate button. The receipt can't
                    // be generated or sent again for this installment.
                    if (params.data.isGeneratedSent === true) {
                        return (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-semibold">
                                <CheckCircle size={13} className="shrink-0" />
                                Sent
                            </span>
                        );
                    }

                    return (
                        <GradientButton
                            onClick={() => handleGenerateReceipt(params)}
                            className={"w-28"}
                            disabled={params.data.isRowDisabled}
                        >
                            Generate
                        </GradientButton>
                    );
                },
                minWidth: 200,
                flex: 1,
                resizable: true,
                sortable: false,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleGenerateReceipt, editingInstallmentId]
    );

    const updatedGridColumns = useMemo(() => {
        return gridColumns.map((col) => {
            if (col.field === "paymentType.type") {
                return {
                    ...col,
                    headerName: "Payment Type",
                    cellRenderer: generatePaymentType,
                };
            }
            return col;
        });
    }, [gridColumns, generatePaymentType]);
    const actionColumns = useMemo(
        () => {
            if (!hasPrivilege("canEdit")) return [];

            return [{
                headerName: "Actions",
                cellRenderer: (params) => (
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(params)}
                            color="black"
                            className="p-2"
                        >
                            <Edit size={17.5} />
                        </Button>
                    </div>
                ),
                width: 180,
                flex: 1
            }];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editingInstallmentId, handleEditClick]
    );

    useEffect(() => {
        const fetchPropertyDetails = async () => {
            try {
                setLoading(true);
                // Reset handover/cancel flags to "unknown" so a previously
                // viewed property's truthy values can't grey the generate/upload
                // controls before this sale's data resolves.
                setIsHandover(null);
                setIsCancelled(null);
                if (!customerSaleId) {
                    throw new Error("No customerSaleId found in params");
                }


                if (projectId) {
                    const projectResponse = await fetchProjects({ projectId });
                    if (projectResponse && projectResponse.length > 0) {

                        const projectCustomerId = projectResponse[0]?.customerId;
                        if (projectCustomerId) {
                            setCustomerId(projectCustomerId); // Store the customerId in state

                            let saleDataDetails;
                            const salesResponse = await getCustomerSales({
                                customerSaleId,
                                customerId: projectCustomerId
                            });

                            if (!salesResponse || !Array.isArray(salesResponse) || salesResponse.length === 0) {
                                throw new Error("Sale data not found");
                            }
                            saleDataDetails = salesResponse[0];

                            let propertyData = saleDataDetails?.propertyData;
                            if (!propertyData) {
                                throw new Error("No property data found either in sale or state.");
                            }
                            setProperty(propertyData);
                            setSale(saleDataDetails);
                            await fetchInstallments(saleDataDetails.customerSalesId);

                            setFormData((prevFormData) => ({
                                ...prevFormData,
                                customerSalesId: saleDataDetails?.customerSalesId,
                            }));

                            setIsHandover(saleDataDetails?.propertyData?.isHandover === true);
                            setIsCancelled(saleDataDetails.cancellationStatus === "Initiated");
                        }
                    }
                }
            } catch (err) {
                console.error("Error fetching property details:", err);
                toast.error(err.message || "Failed to fetch property details");
                setError(err.message || "Failed to fetch property details");
            } finally {
                setLoading(false);
            }
        };

        if (customerSaleId && projectId) {
            fetchPropertyDetails();
        } else {
            setLoading(false);
            setError("Sale data or Project ID not found");
            toast.error("Sale data or Project ID not found");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerSaleId, projectId]);

    const onSaleUpdate = async (state) => {
        // Callers may invoke this with no args (just to refetch) or with
        // { activeTab } to also switch tabs. Guard against undefined.
        if (state?.activeTab) setActiveTab(state.activeTab);
        try {
            setLoading(true);
            if (!customerSaleId) {
                throw new Error("No customerSaleId found to fetch data");
            }

            let saleDataDetails;
            const salesResponse = await getCustomerSales({
                customerSaleId,
            });
            if (
                !salesResponse ||
                !Array.isArray(salesResponse) ||
                salesResponse.length === 0
            ) {
                throw new Error("Sale data not found");
            }
            saleDataDetails = salesResponse[0];

            const propertyData = saleDataDetails?.propertyData;
            if (!propertyData) {
                throw new Error("Property not found");
            }
            setProperty(propertyData);
            setSale(saleDataDetails);
            await fetchInstallments(saleDataDetails.customerSalesId);
            setFormData((prevFormData) => ({
                ...prevFormData,
                customerSalesId: saleDataDetails?.customerSalesId,
            }));

            setIsHandover(saleDataDetails?.propertyData?.isHandover === true);
            setIsCancelled(saleDataDetails.cancellationStatus === "Initiated");
        } catch (err) {
            console.error("Error fetching property details:", err);
            toast.error(err.message || "Failed to fetch property details");
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[calc(100vh-4rem)]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-flamingo-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 sm:p-6 lg:p-8">
                <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
                    {error}
                </div>
            </div>
        );
    }

    const tabData = [
        {
            label: "Property & user",
            shortLabel: "Property",
            value: "property",
            icon: Home,
            content: (
                <PropertyDetailsTab
                    customerId={customerId}
                    property={property}
                    sale={sale}
                    onSaleUpdate={onSaleUpdate}
                />
            ),
        },
        {
            label: "Installments",
            value: "installments",
            icon: CreditCard,
            content: (
                <PaymentInstallmentsTab
                    installments={installments}
                    gridColumns={updatedGridColumns}
                    actionColumns={actionColumns}
                    sale={sale}
                    filteredcustomerId={customerSaleId}
                    isHandover={isHandover}
                    setIsHandover={setIsHandover}
                    isCancelled={isCancelled}
                    isRowEditing={isRowEditing}
                    setInstallments={setInstallments}
                    refreshNonce={installmentsRefreshNonce}
                    setHasUnsavedScheduleInstallmentChanges={setHasUnsavedScheduleInstallmentChanges}
                />
            ),
        },
        showCrmDocTabs && {
            label: "Agreements",
            value: "agreements",
            icon: FileText,
            content: (
                <AgreementsTab
                    isHandover={isHandover}
                    sale={sale}
                    onSaleUpdate={onSaleUpdate}
                    isCancelled={isCancelled}
                />
            ),
        },
        showCrmDocTabs && sale?.cancellationStatus !== null && {
            label: "Cancellation",
            value: "cancellation",
            icon: AlertTriangle,
            content: (
                <CancellationDetailsTab
                    sale={sale}
                    customerSaleId={sale?.customerSalesId}
                    installments={installments}
                />
            ),
        },
        showCrmDocTabs && {
            label: "Receipts",
            value: "receipts",
            icon: Receipt,
            content: (
                <Receipts
                    formData={formData}
                    handleInputChange={handleInputChange}
                    setFormData={setFormData}
                    isHandover={isHandover}
                    isCancelled={isCancelled}
                />
            ),
        },
        showCrmDocTabs && {
            label: "Notifications",
            value: "history",
            icon: Bell,
            content: (
                <CustomerNotificationHistory
                    property={property}
                />
            ),
        },
        // F16 (v2): Unit Execution Dashboard tab (interior engine — Brief 3)
        property?.propertyId && {
            label: "Execution",
            value: "execution",
            icon: ClipboardList,
            content: (
                <UnitExecutionDashboard propertyId={property.propertyId} />
            ),
        },
    ].filter(Boolean);

    // Derived display values for the hero band + KPI tiles.
    const fmtINR = (n) => {
        const v = Number(n || 0);
        if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
        if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
        if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
        return `₹${Math.round(v)}`;
    };
    const fmtDate = (d) => {
        if (!d) return "—";
        try {
            return new Date(d).toLocaleDateString("en-IN", {
                day: "numeric", month: "short", year: "numeric",
            });
        } catch { return "—"; }
    };
    const customerName = `${sale?.user?.firstName || ""} ${sale?.user?.lastName || ""}`.trim();
    const projectName = property?.projectPhase?.project?.projectName || "Project";
    const phaseName = property?.projectPhase?.name;
    const propertyNo = property?.propertyNo;
    const buildingNo = property?.buildingNo;
    const saleStatusLabel = isCancelled
        ? "CANCELLATION INITIATED"
        : sale?.cancellationStatus === "Completed"
        ? "CANCELLED"
        : "ACTIVE";
    const saleStatusCls = isCancelled
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : sale?.cancellationStatus === "Completed"
        ? "bg-rose-50 text-rose-700 border-rose-200"
        : "bg-emerald-50 text-emerald-700 border-emerald-200";
    const saleStatusDotCls = isCancelled
        ? "bg-amber-500"
        : sale?.cancellationStatus === "Completed"
        ? "bg-rose-500"
        : "bg-emerald-500";

    const handleTabClick = (value) => {
        if (
            activeTab === "installments" &&
            value !== "installments" &&
            hasUnsavedScheduleInstallmentChanges
        ) {
            toast.warning("Please save changes");
            return;
        }
        setActiveTab(value);
        if (value === "receipts" && sale?.customerSalesId) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                customerSalesId: sale?.customerSalesId,
            }));
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
            {/* Back — pinned above the hero for context. */}
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="self-start inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium"
            >
                <ArrowLeft size={16} />
                Back
            </button>

            {/* Slate-gradient hero — project + unit + buyer at a glance, with
                sale status pill on the right. Matches the rest of the app's
                detail headers (project details, dashboard, reports). */}
            <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-flamingo-500/20 border border-flamingo-500/30 flex items-center justify-center shrink-0">
                            <Building2 size={20} className="text-flamingo-300" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs uppercase tracking-wider text-slate-300">
                                Sold property
                            </p>
                            <h1 className="text-lg sm:text-xl font-semibold tracking-tight mt-0.5 truncate">
                                {projectName}
                                {phaseName ? <span className="text-slate-300 font-normal"> · {phaseName}</span> : null}
                            </h1>
                            <p className="text-xs text-slate-300 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                {propertyNo && (
                                    <span className="inline-flex items-center gap-1">
                                        <Home size={11} />
                                        Unit <span className="font-semibold text-white">{propertyNo}</span>
                                        {buildingNo ? <span className="text-slate-400"> · Bldg {buildingNo}</span> : null}
                                    </span>
                                )}
                                {customerName && (
                                    <span className="inline-flex items-center gap-1">
                                        <UserIcon size={11} />
                                        {customerName}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border ${saleStatusCls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${saleStatusDotCls}`} />
                            {saleStatusLabel}
                        </span>
                        {isHandover && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border bg-indigo-50 text-indigo-700 border-indigo-200">
                                <ClipboardCheck size={11} />
                                HANDED OVER
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* KPI strip — sale fundamentals, scannable at a glance. */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-flamingo-50 text-flamingo-600 flex items-center justify-center shrink-0">
                        <Wallet size={18} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Agreement value</p>
                        <p className="text-base font-bold text-slate-900 tabular-nums truncate" title={`₹${Number(sale?.basePrice || 0).toLocaleString("en-IN")}`}>
                            {fmtINR(sale?.basePrice)}
                        </p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                        <Calendar size={18} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Sale date</p>
                        <p className="text-base font-semibold text-slate-900 truncate">{fmtDate(sale?.saleDate)}</p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                        <CreditCard size={18} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Payment option</p>
                        <p className="text-base font-semibold text-slate-900 truncate" title={sale?.paymentOption || ""}>
                            {sale?.paymentOption ? sale.paymentOption.replaceAll("_", " ") : "—"}
                        </p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                        <CreditCard size={18} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Installments</p>
                        <p className="text-base font-semibold text-slate-900 tabular-nums">
                            {installments?.length || 0}
                        </p>
                    </div>
                </div>
            </div>

            {/* Pill tab nav — matches dashboard / reports / approvals. */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 inline-flex gap-1 self-start flex-wrap">
                {tabData.map(({ value, label, icon: Icon }) => (
                    <button
                        key={value}
                        type="button"
                        onClick={() => handleTabClick(value)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                            activeTab === value
                                ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                        }`}
                    >
                        {Icon && <Icon size={14} />}
                        {label}
                    </button>
                ))}
            </div>

            {/* Tab body — wrapped in a card for consistency. */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className="p-4 sm:p-5">
                    {tabData.find((item) => item.value === activeTab)?.content}
                </div>
            </div>

            {/* Payment receipt preview (5 payment types) — single
                polymorphic modal, formerly five copy-pasted FormModal files. */}
            {modalIsOpen && PAYMENT_RECEIPT_TYPES.has(receiptType) && (
                <PaymentReceiptPreviewModal
                    open={modalIsOpen}
                    onClose={closeModal}
                    paymentType={receiptType}
                    data={receiptContent}
                    onSendDocument={() => handleSendReceipt(receiptContent)}
                    sendLoading={sspLoading}
                />
            )}

            {/* Editable receipt fallback — Quill editor for free-form receipt
                content. Different UX from the 5 preview-iframe modals so it
                stays separate, but uses the same FormModal shell. */}
            {modalIsOpen && !PAYMENT_RECEIPT_TYPES.has(receiptType) && (
                <FormModal
                    open={modalIsOpen}
                    onClose={closeModal}
                    title="Receipt Preview"
                    size="xl"
                    footer={
                        <div className="flex justify-end">
                            <PrimaryButton
                                onClick={() => handleSendReceipt(receiptContent)}
                                disabled={sspLoading}
                            >
                                {sspLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : "Send"}
                            </PrimaryButton>
                        </div>
                    }
                >
                    <p className="text-gray-600 mb-4">
                        Edit the receipt content below and click send to notify the user,
                        or close to cancel.
                    </p>
                    <ReactQuill
                        value={receiptContent}
                        onChange={handleQuillChange}
                        theme="snow"
                    />
                </FormModal>
            )}

            {/* SSP Passphrase Modal */}
            <SSPPassphraseModal
                isOpen={showSSPModal}
                onClose={handleSSPClose}
                onConfirm={handleSSPConfirm}
                loading={sspLoading}
            />
        </div>
    );
};

export default SoldPropertyDetails;